import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@algoan/dashboard/store/store';
import { parseJwtPayload } from '@algoan/dashboard/utils';
import { AccessTokenPayload, ResourceAccess, UserInformation, Roles } from '@algoan/dashboard/model';
import { convertAccessTokenPayloadToUserInformation } from '@algoan/dashboard/converters';

export const isUserConnectedSelector = (state: RootState): boolean => !!state.auth.authInformation?.access_token;

export const accessTokenPayloadSelector: (state: RootState) => AccessTokenPayload | undefined = createSelector(
  (state: RootState) => state.auth.authInformation?.access_token,
  (accessToken: string | undefined): AccessTokenPayload | undefined =>
    accessToken ? parseJwtPayload(accessToken) : undefined,
);

export const selectUserInformation: (state: RootState) => UserInformation | undefined = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload): UserInformation | undefined => {
    if (accessTokenPayload === undefined) {
      return undefined;
    }

    return convertAccessTokenPayloadToUserInformation(accessTokenPayload);
  },
);

export const accessTokenUserIdSelector: (state: RootState) => string | undefined = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload: AccessTokenPayload | undefined): string | undefined => accessTokenPayload?.sub,
);
export const accessTokenProjectsSelector: (state: RootState) => { [key: string]: string } = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload: AccessTokenPayload | undefined): { [key: string]: string } => accessTokenPayload?.projects ?? {},
);

export const projectNameSelector: (state: RootState, projectId: string) => string | undefined = createSelector(
  accessTokenProjectsSelector,
  (_state: RootState, projectId: string) => projectId,
  (projects: { [key: string]: string }, projectId): string | undefined => projects[projectId],
);

export const accessTokenTeamSelector: (state: RootState) => string | undefined = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload: AccessTokenPayload | undefined): string | undefined => accessTokenPayload?.team,
);

export const accessTokenRolesSelector: (state: RootState, role: keyof ResourceAccess) => Roles | undefined =
  createSelector(
    accessTokenPayloadSelector,
    (_: RootState, role: keyof ResourceAccess) => role,
    (accessTokenPayload, role) => accessTokenPayload?.resource_access?.[role],
  );

export const accessTokenOrganizationIdSelector: (state: RootState) => string | undefined = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload: AccessTokenPayload | undefined): string | undefined => accessTokenPayload?.organizationIds,
);

export const accessTokenProjectIdSelector: (state: RootState) => string | undefined = createSelector(
  accessTokenPayloadSelector,
  (accessTokenPayload: AccessTokenPayload | undefined): string | undefined =>
    accessTokenPayload?.projectIds ?? accessTokenPayload?.chatflowIds,
);

export const selectUsername: (state: RootState) => string | undefined = createSelector(
  selectUserInformation,
  (userInformation): string | undefined => userInformation?.preferredUsername,
);
